import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import { useTheme } from '../../theme';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Platform,
  TextInput,
  Image,
  ActivityIndicator,
  Modal,
  Pressable,
  useWindowDimensions,
  BackHandler,
} from 'react-native';
import { useSelector } from 'react-redux';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { useTranslation } from 'react-i18next';
import FontFamily from '../../constants/fonts';
import { AdminColors } from '../Admin/constants/adminColors';
import LhButton from '../../components/LhButton';
import { DeliveryListItem, DeliveryDetail, DeliveryStatus, EquipmentValue, CheckerVerificationRequest, RouteTypeItem } from './types';
import { fetchDeliveryDetail, fetchEquipmentConfigItems, fetchPhotoCategories, fetchActiveRouteTypes, submitCheckerVerification } from './perfectTruckApi';
import { EquipmentConfigItem, PhotoCategoryItem } from '../Admin/pages/PerfectTruckManagement/types';
import UnsavedChangesDialog from './UnsavedChangesDialog';
import { canSubmitCheckerVerification } from './perfectTruckRoles';
import { formatDate, formatUserName, formatTimeAmPm, resolveRouteTypeLabel, resolveRouteTypeId } from './perfectTruckUtils';
import { useToast } from '../../components/LhToastContainer';
import PTPhotoUpload from './PTPhotoUpload';
import { WebImage } from './PTPhotoUpload';
import type { RootState } from '../../store';
import { useDarkStyles, useDarkColor } from '../../theme/useDarkStyles';

const STATUS_CONFIG: Record<DeliveryStatus, { bg: string; color: string }> = {
  'Pending Check':                { bg: AdminColors.yellow50,  color: AdminColors.yellow200 },
  'In Progress':                  { bg: AdminColors.blue50,    color: AdminColors.blue700 },
  'In Progress - Certifier':      { bg: AdminColors.blue50,    color: AdminColors.blue700 },
  'In Progress - Checker':        { bg: AdminColors.blue50,    color: AdminColors.blue700 },
  'Approved':                     { bg: AdminColors.green75,   color: AdminColors.green600 },
  'Rejected':                     { bg: AdminColors.red75,     color: AdminColors.red400 },
  'Approved - No Certification':  { bg: AdminColors.green75,   color: AdminColors.green600 },
  'Rejected - No Certification':  { bg: AdminColors.red75,     color: AdminColors.red400 },
};

// Dark-theme variants: solid dark tinted backgrounds with white text.
const STATUS_CONFIG_DARK: Record<DeliveryStatus, { bg: string; color: string }> = {
  'Pending Check':                { bg: '#92600A', color: '#FFFFFF' },
  'In Progress':                  { bg: '#1A5BB5', color: '#FFFFFF' },
  'In Progress - Certifier':      { bg: '#1A5BB5', color: '#FFFFFF' },
  'In Progress - Checker':        { bg: '#1A5BB5', color: '#FFFFFF' },
  'Approved':                     { bg: '#1A6B24', color: '#FFFFFF' },
  'Rejected':                     { bg: '#9B2626', color: '#FFFFFF' },
  'Approved - No Certification':  { bg: '#1A6B24', color: '#FFFFFF' },
  'Rejected - No Certification':  { bg: '#9B2626', color: '#FFFFFF' },
};

const normalizeEquipmentRouteTypes = (routeType: EquipmentConfigItem['routeType']): string[] => {
  const rawValues = Array.isArray(routeType)
    ? routeType.map(v => String(v).trim())
    : !routeType
      ? []
      : String(routeType)
          .split(',')
          .map(v => v.trim());

  const hasAll = rawValues.some(v => v === '' || v.toLowerCase() === 'all');
  if (hasAll) {
    return [];
  }

  return rawValues.filter(Boolean);
};

const isAllRouteType = (routeTypes: string[]): boolean => {
  if (routeTypes.length === 0) {
    return true;
  }

  return routeTypes.some(rt => rt.toLowerCase() === 'all');
};

const filterEquipmentByRouteType = (
  items: EquipmentConfigItem[],
  selectedRouteType?: string | null,
  preservedEquipmentNames: string[] = [],
  routeTypeConfig: RouteTypeItem[] = [],
): EquipmentConfigItem[] => {
  if (!selectedRouteType) {
    return items;
  }

  const selected = resolveRouteTypeId(selectedRouteType, routeTypeConfig).toLowerCase();
  const preservedNamesSet = new Set(preservedEquipmentNames.map(name => name.toLowerCase()));

  return items.filter(item => {
    if (preservedNamesSet.has(item.equipmentName.toLowerCase())) {
      return true;
    }

    const configuredRoutes = normalizeEquipmentRouteTypes(item.routeType);
    if (isAllRouteType(configuredRoutes)) {
      return true;
    }

    return configuredRoutes.some(rt => resolveRouteTypeId(rt, routeTypeConfig).toLowerCase() === selected);
  });
};

const normalizePhotoRouteTypes = (routeType: PhotoCategoryItem['routeType']): string[] => {
  const rawValues = Array.isArray(routeType)
    ? routeType.map(v => String(v).trim())
    : !routeType
      ? []
      : String(routeType)
          .split(',')
          .map(v => v.trim());

  const hasAll = rawValues.some(v => v === '' || v.toLowerCase() === 'all');
  if (hasAll) {
    return [];
  }

  return rawValues.filter(Boolean);
};

const filterPhotoCategoriesByRouteType = (
  items: PhotoCategoryItem[],
  selectedRouteType?: string | null,
  preservedPhotoLabels: string[] = [],
  routeTypeConfig: RouteTypeItem[] = [],
): PhotoCategoryItem[] => {
  if (!selectedRouteType) {
    return items;
  }

  const selected = resolveRouteTypeId(selectedRouteType, routeTypeConfig).toLowerCase();
  const preservedLabelsSet = new Set(preservedPhotoLabels.map(label => label.toLowerCase()));

  return items.filter(item => {
    if (preservedLabelsSet.has(item.photoLabel.toLowerCase())) {
      return true;
    }

    const configuredRoutes = normalizePhotoRouteTypes(item.routeType);
    if (isAllRouteType(configuredRoutes)) {
      return true;
    }

    return configuredRoutes.some(rt => resolveRouteTypeId(rt, routeTypeConfig).toLowerCase() === selected);
  });
};

const normalizeResponseKey = (value: unknown): string => String(value ?? '').trim().toLowerCase();

type StoredCheckerResponseValue = 'pos' | 'neg';
type StoredCheckerResponseEntry = StoredCheckerResponseValue | { value: StoredCheckerResponseValue; remarks?: string };

const readStoredCheckerSelection = (
  stored: unknown,
  positiveLabel: string,
  negativeLabel: string,
): { value: EquipmentValue; remarks: string } => {
  let rawValue: unknown = stored;
  let remarks = '';

  if (stored && typeof stored === 'object' && !Array.isArray(stored)) {
    const candidate = stored as { value?: unknown; remarks?: unknown };
    rawValue = candidate.value;
    if (typeof candidate.remarks === 'string') remarks = candidate.remarks;
  }

  const normalized = normalizeResponseKey(rawValue);
  const normalizedPositive = normalizeResponseKey(positiveLabel);
  const normalizedNegative = normalizeResponseKey(negativeLabel);

  if (normalized === 'pos' || normalized === 'yes' || (normalizedPositive && normalized === normalizedPositive)) {
    return { value: 'yes', remarks };
  }

  if (normalized === 'neg' || normalized === 'no' || (normalizedNegative && normalized === normalizedNegative)) {
    return { value: 'no', remarks };
  }

  return { value: null, remarks };
};

const serializeCheckerResponseEntry = (
  item: EquipmentConfigItem | null,
  value: EquipmentValue,
  remarks: string,
): StoredCheckerResponseEntry | null => {
  if (value === 'yes') {
    if (item?.isTextBoxRequired || remarks) {
      return { value: 'pos', remarks };
    }
    return 'pos';
  }

  if (value === 'no') {
    if (item?.isTextBoxRequired || remarks) {
      return { value: 'neg', remarks };
    }
    return 'neg';
  }

  return null;
};

const parseCheckerDisplayItems = (
  detail: DeliveryDetail,
  equipmentConfig: EquipmentConfigItem[],
): Array<{ question: string; response: string; remarks?: string | null }> => {
  if (!detail.checkerResponse) return [];
  try {
    const checkerMap = JSON.parse(detail.checkerResponse) as Record<string, unknown>;
    const configById = new Map(equipmentConfig.map(item => [normalizeResponseKey(item.id), item] as const));
    const configByName = new Map(equipmentConfig.map(item => [normalizeResponseKey(item.equipmentName), item] as const));

    return Object.entries(checkerMap).flatMap(([key, value]) => {
      const configItem = configById.get(normalizeResponseKey(key)) ?? configByName.get(normalizeResponseKey(key));
      const isNumericKey = /^\d+$/.test(String(key).trim());
      if (!configItem && isNumericKey) return [];

      const question = configItem?.equipmentName ?? key;
      let responseText = 'N/A';
      let remarksText: string | null = null;

      const convertStoredValue = (storedValue: unknown): string => {
        const normalized = normalizeResponseKey(storedValue);
        if (normalized === 'pos' && configItem) return configItem.checkerPosLabel;
        if (normalized === 'neg' && configItem) return configItem.checkerNegLabel;
        return String(storedValue ?? 'N/A');
      };

      if (value === null || value === undefined) {
        responseText = 'N/A';
      } else if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        responseText = convertStoredValue(value);
      } else if (typeof value === 'object') {
        const storedValue = (value as { value?: unknown }).value;
        responseText = convertStoredValue(storedValue);
        const remarks = (value as { remarks?: unknown }).remarks;
        if (remarks) remarksText = String(remarks);
      }

      return [{ question, response: responseText, remarks: remarksText }];
    });
  } catch (e) {
    console.warn('Failed to parse checker response:', e);
    return [];
  }
};

const parseCertifierDisplayItems = (
  detail: DeliveryDetail,
  equipmentConfig: EquipmentConfigItem[],
): Array<{ question: string; response: string; remarks?: string | null }> => {
  if (!detail.certifierResponse) {
    return [];
  }

  try {
    const certMap = JSON.parse(detail.certifierResponse) as Record<string, unknown>;
    const configById = new Map(equipmentConfig.map(item => [normalizeResponseKey(item.id), item] as const));
    const configByName = new Map(equipmentConfig.map(item => [normalizeResponseKey(item.equipmentName), item] as const));

    return Object.entries(certMap).flatMap(([key, value]) => {
      const configItem = configById.get(normalizeResponseKey(key)) ?? configByName.get(normalizeResponseKey(key));
      const isNumericKey = /^\d+$/.test(String(key).trim());

      if (!configItem && isNumericKey) {
        return [];
      }

      const question = configItem?.equipmentName ?? key;
      let responseText = 'N/A';
      let remarksText: string | null = null;

      const convertStoredValue = (storedValue: unknown): string => {
        const normalized = normalizeResponseKey(storedValue);
        if (normalized === 'pos' && configItem) {
          return configItem.certifierPosLabel;
        }
        if (normalized === 'neg' && configItem) {
          return configItem.certifierNegLabel;
        }
        return String(storedValue ?? 'N/A');
      };

      if (value === null || value === undefined) {
        responseText = 'N/A';
      } else if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        responseText = convertStoredValue(value);
      } else if (typeof value === 'object') {
        const storedValue = (value as { value?: unknown }).value;
        responseText = convertStoredValue(storedValue);
        const remarks = (value as { remarks?: unknown }).remarks;
        if (remarks) {
          remarksText = String(remarks);
        }
      }

      return [{ question, response: responseText, remarks: remarksText }];
    });
  } catch (error) {
    console.warn('Failed to parse certifier response:', error);
    return [];
  }
};

// ── Sub-components ────────────────────────────────────────────────────────────

interface CheckerRowProps {
  item: EquipmentConfigItem;
  value: EquipmentValue;
  onChange: (id: number, val: EquipmentValue) => void;
  isMobile?: boolean;
  alt?: boolean;
  hasDetails?: boolean;
}

const CheckerRow: React.FC<CheckerRowProps> = ({ item, value, onChange, isMobile, alt, hasDetails }) => {
  const styles = useDarkStyles(baseStyles);
  return (
    <View style={[
      styles.equipmentRow,
      isMobile && styles.equipmentRowMobile,
      isMobile && alt && styles.equipmentRowMobileAlt,
      hasDetails && { borderBottomWidth: 0 },
    ]}>
      <Text style={styles.equipmentLabel}>{item.equipmentName}</Text>
      <View style={styles.equipmentOpts}>
        <TouchableOpacity
          style={[styles.eqOption, value === 'yes' && styles.eqOptionYes]}
          onPress={() => onChange(item.id, value === 'yes' ? null : 'yes')}
        >
          <Text style={[styles.eqOptionText, value === 'yes' && styles.eqOptionTextSelected]}>
            {item.checkerPosLabel}
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.eqOption, value === 'no' && styles.eqOptionNo]}
          onPress={() => onChange(item.id, value === 'no' ? null : 'no')}
        >
          <Text style={[styles.eqOptionText, value === 'no' && styles.eqOptionTextSelected]}>
            {item.checkerNegLabel}
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

export interface PerfectTruckReviewHandle {
  handleNavigationAttempt: (callback: () => void) => void;
}

interface PerfectTruckReviewProps {
  record?: DeliveryListItem;
  onBack: () => void;
  onApprove: () => void;
  onReject: () => void;
  forceReadOnly?: boolean;
}

const PerfectTruckReview = forwardRef<PerfectTruckReviewHandle, PerfectTruckReviewProps>(({
  record,
  onBack,
  onApprove,
  onReject,
  forceReadOnly = false,
}, ref) => {
  const dc = useDarkColor();
  const styles = useDarkStyles(baseStyles);
  const { t, i18n } = useTranslation('perfectTruck');
  const { resolvedTheme } = useTheme();

  const currentLanguage = (i18n.language ?? 'en').split('-')[0];
  const [detail, setDetail] = useState<DeliveryDetail | null>(null);
  const [checkerItems, setCheckerItems] = useState<EquipmentConfigItem[]>([]);
  const [equipmentConfig, setEquipmentConfig] = useState<EquipmentConfigItem[]>([]);
  const [routeTypes, setRouteTypes] = useState<RouteTypeItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [apiError, setApiError] = useState('');

  // Checker form state
  const [checkerEquipment, setCheckerEquipment] = useState<Record<number, EquipmentValue>>({});
  const [truckOrganized, setTruckOrganized] = useState<EquipmentValue>(null);
  const [comments, setComments] = useState('');
  const [checkerRemarks, setCheckerRemarks] = useState<Record<number, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Checker photo state
  const [photoCategories, setPhotoCategories] = useState<PhotoCategoryItem[]>([]);
  const [checkerImageFileNames, setCheckerImageFileNames] = useState<Record<string, string>>({});
  const [checkerImageUrls, setCheckerImageUrls] = useState<Record<string, string>>({});

  // Image preview state
  const [previewImageUri, setPreviewImageUri] = useState<string | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  // Unsaved changes state
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  // Stale record dialog state
  const [showStaleDialog, setShowStaleDialog] = useState(false);
  const [staleMessage, setStaleMessage] = useState('');

  const toast = useToast();

  const moduleAccess = useSelector((state: RootState) => state.appData.moduleAccess);
  const isPerfectTruckReadOnly = moduleAccess?.find(m => m.moduleName === 'Perfect Truck')?.readOnly ?? false;
  const canSubmit = canSubmitCheckerVerification(isPerfectTruckReadOnly);
  const roleId = useSelector((state: RootState) => state.appData?.userInfo?.roleId);
  //const canSubmit = canSubmitCheckerVerification(roleId) && !forceReadOnly;

  const isMobile = Platform.OS !== 'web';
  const windowWidth = useWindowDimensions().width;
  const windowHeight = useWindowDimensions().height;
  const modalImageSize = Math.min(windowWidth * 0.9, windowHeight * 0.7);

  // Photo card width measurement for horizontal scroll logic (mirrors PerfectTruckCreate)
  const [certifierCardWidth, setCertifierCardWidth] = useState(0);
  const [checkerCardWidth, setCheckerCardWidth] = useState(0);
  const CARD_PADDING = isMobile ? 28 : 40; // cardMobile: 14+14, card: 20+20
  const MAX_VISIBLE = 3;

  const loadData = useCallback(async () => {
    if (!record) {
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      setApiError('');
      const [deliveryDetail, eqData, photoCats, rtData] = await Promise.all([
        fetchDeliveryDetail(record.id),
        fetchEquipmentConfigItems({ language: currentLanguage }),
        fetchPhotoCategories(currentLanguage),
        fetchActiveRouteTypes(currentLanguage),
      ]);
      setDetail(deliveryDetail);
      setEquipmentConfig(eqData);
      setRouteTypes(rtData);

      // routeType is persisted as a language-independent ID (legacy records store the
      // label); resolve it to a label for display and to a canonical ID for filtering.
      const resolvedRouteTypeLabel = resolveRouteTypeLabel(deliveryDetail.routeType, rtData) || null;
      const resolvedRouteTypeId = resolveRouteTypeId(deliveryDetail.routeType, rtData) || null;

      // Parse delivery response keys before filtering so that inactive config
      // items still referenced in the delivery are retained in the UI.
      let checkerResponseKeys = new Set<string>();
      let checkerImageResponseKeys = new Set<string>();
      if (deliveryDetail.checkerResponse) {
        try {
          const checkerMap = JSON.parse(deliveryDetail.checkerResponse) as Record<string, unknown>;
          const normalizedCheckerMap = new Map<string, unknown>(
            Object.entries(checkerMap).map(([k, v]) => [normalizeResponseKey(k), v]),
          );
          checkerResponseKeys = new Set(
            eqData
              .filter(item => normalizedCheckerMap.has(normalizeResponseKey(item.id)) || normalizedCheckerMap.has(normalizeResponseKey(item.equipmentName)))
              .map(item => item.equipmentName),
          );
        } catch {}
      }
      if (deliveryDetail.checkerImageResponse) {
        try {
          const checkerImageMap = JSON.parse(deliveryDetail.checkerImageResponse) as Record<string, unknown>;
          checkerImageResponseKeys = new Set(Object.keys(checkerImageMap).map(key => key.toLowerCase()));
          const parsedImgKeys = Object.keys(JSON.parse(deliveryDetail.checkerImageResponse) as Record<string, unknown>);
          // Build lookup from photo category id -> photoLabel for resolution
          const pcById = new Map((photoCats || []).map(p => [String(p.id), p.photoLabel.trim().toLowerCase()] as const));
          checkerImageResponseKeys = new Set(
            parsedImgKeys.flatMap(k => {
              const raw = String(k).trim().toLowerCase();
              // Include both the raw key and, when it is a numeric id, the resolved label
              const resolved = /^\d+$/.test(raw) ? pcById.get(raw) : undefined;
              return resolved ? [raw, resolved] : [raw];
            }),
          );
        } catch {}
      }

      // Parse certifier response keys for "Pending Check" fallback
      let certResponseKeys = new Set<string>();
      if (deliveryDetail?.certifierResponse) {
        try {
          const certMap = JSON.parse(deliveryDetail.certifierResponse) as Record<string, unknown>;
          const normalizedCertMap = new Map<string, unknown>(
            Object.entries(certMap).map(([k, v]) => [normalizeResponseKey(k), v]),
          );
          certResponseKeys = new Set(
            eqData
              .filter(item => normalizedCertMap.has(normalizeResponseKey(item.id)) || normalizedCertMap.has(normalizeResponseKey(item.equipmentName)))
              .map(item => item.equipmentName),
          );
        } catch {}
      }

      // // SNAPSHOT LOGIC: Use equipment config snapshot if available (preserves original config)
      // // Priority: checkerConfigSnapshot > certifierConfigSnapshot > current config
      // let checker: EquipmentConfigItem[];
      // if (deliveryDetail?.checkerConfigSnapshot) {
      //   // Use snapshot captured when checker started
      //   try {
      //     const snapshotItems = JSON.parse(deliveryDetail.checkerConfigSnapshot) as EquipmentConfigItem[];
      //     checker = snapshotItems.sort((a, b) => a.displayOrder - b.displayOrder);
      //   } catch (e) {
      //     console.warn('Failed to parse checkerConfigSnapshot, trying certifierConfigSnapshot');
      //     // Fallback to certifierConfigSnapshot if available
      //     if (deliveryDetail.certifierConfigSnapshot) {
      //       try {
      //         const snapshotItems = JSON.parse(deliveryDetail.certifierConfigSnapshot) as EquipmentConfigItem[];
      //         checker = snapshotItems.filter(i => i.showOnCheckerForm).sort((a, b) => a.displayOrder - b.displayOrder);
      //       } catch (e2) {
      //         console.warn('Failed to parse certifierConfigSnapshot, using current config');
      //         checker = eqData.filter(i => i.showOnCheckerForm && i.isActive).sort((a, b) => a.displayOrder - b.displayOrder);
      //       }
      //     } else {
      //       checker = eqData.filter(i => i.showOnCheckerForm && i.isActive).sort((a, b) => a.displayOrder - b.displayOrder);
      //     }
      //   }
      // } else if (deliveryDetail?.certifierConfigSnapshot) {
      //   // No checkerConfigSnapshot yet: Use certifierConfigSnapshot (checker should see what certifier saw)
      //   try {
      //     const snapshotItems = JSON.parse(deliveryDetail.certifierConfigSnapshot) as EquipmentConfigItem[];
      //     // Filter to only items marked for checker form
      //     checker = snapshotItems.filter(i => i.showOnCheckerForm).sort((a, b) => a.displayOrder - b.displayOrder);
      //   } catch (e) {
      //     console.warn('Failed to parse certifierConfigSnapshot, using current config');
      //     checker = eqData.filter(i => i.showOnCheckerForm && i.isActive).sort((a, b) => a.displayOrder - b.displayOrder);
      //   }
      // } else {
      //   // No snapshots (old certificates): Show equipment that certifier answered + active items
      //   // For "Pending Check", this shows what certifier saw (using certResponseKeys)
        const checker = eqData
          .filter(i => i.showOnCheckerForm && (i.isActive || certResponseKeys.has(i.equipmentName) || checkerResponseKeys.has(i.equipmentName)))
          .sort((a, b) => a.displayOrder - b.displayOrder);
      //}
        let resolvedCheckerItems = checker;
      
      let activePhotos = (photoCats || [])
        .filter(p => p.isActive || checkerImageResponseKeys.has(String(p.id)) || checkerImageResponseKeys.has(p.photoLabel.trim().toLowerCase()))
        .sort((a, b) => a.displayOrder - b.displayOrder);
      
      // Apply route type filtering to photo categories, preserving items in response keys
      activePhotos = filterPhotoCategoriesByRouteType(
        activePhotos,
        resolvedRouteTypeId,
        Array.from(checkerImageResponseKeys),
        rtData,
      );
      
      setPhotoCategories(activePhotos);

      // Restore draft form data if record was saved as In Progress - Checker
      // IMPORTANT: use local `checker` variable (freshly loaded), NOT `checkerItems` state
      // which is still stale/empty at this point due to async setState
      if (deliveryDetail.checkerResponse) {
        try {
          const checkerMapRaw = JSON.parse(deliveryDetail.checkerResponse) as Record<string, unknown>;
          // Normalize keys for lookup (id or equipmentName)
          const normalizedCheckerMap = new Map<string, unknown>(
            Object.entries(checkerMapRaw).map(([k, v]) => [normalizeResponseKey(k), v])
          );

          const equipMap: Record<number, EquipmentValue> = {};
          checker.forEach(item => {
            const stored = normalizedCheckerMap.get(normalizeResponseKey(item.id)) ?? normalizedCheckerMap.get(normalizeResponseKey(item.equipmentName));
            const parsed = readStoredCheckerSelection(stored, item.checkerPosLabel, item.checkerNegLabel);
            if (parsed.value) equipMap[item.id] = parsed.value;
            if (parsed.remarks) setCheckerRemarks(prev => ({ ...prev, [item.id]: parsed.remarks }));
          });

          // Fallback: add synthetic items for legacy name-only entries or hard-deleted items
          const configCheckerNames = new Set(checker.map(item => normalizeResponseKey(item.equipmentName)));
          const configById = new Map(checker.map(item => [normalizeResponseKey(item.id), item] as const));
          const extraCheckerItems: EquipmentConfigItem[] = [];
          let syntheticCheckerId = -1000;

          Object.entries(checkerMapRaw).forEach(([rawKey, storedLabel]) => {
            const key = normalizeResponseKey(rawKey);
            // If key corresponds to a numeric id already handled, skip
            if (configById.has(key)) return;
            if (configCheckerNames.has(key)) return;
            // If key looks numeric but not in config, skip to avoid creating synthetic for ids
            if (/^\d+$/.test(String(rawKey).trim())) return;
            // If storedLabel is empty/null, skip
            const valueOnly = (storedLabel && typeof storedLabel === 'object' && !Array.isArray(storedLabel))
              ? (storedLabel as { value?: unknown }).value
              : storedLabel;
            if (valueOnly == null || valueOnly === '') return;

            const id = syntheticCheckerId--;
            extraCheckerItems.push({
              id,
              equipmentName: rawKey,
              checkerPosLabel: String(valueOnly),
              checkerNegLabel: '—',
              certifierPosLabel: '',
              certifierNegLabel: '',
              showOnCertifierForm: false,
              showOnCheckerForm: true,
              displayOrder: 9999,
              isActive: false,
              isDuo: false,
              isFsv: false,
              organizationType: null,
              is3pl: false,
            });
            equipMap[id] = 'yes';
          });

          if (extraCheckerItems.length > 0) {
            resolvedCheckerItems = [...checker, ...extraCheckerItems];
          }

          setCheckerEquipment(equipMap);
        } catch (e) {
          console.warn('Failed to parse checkerResponse:', e);
        }
      }
      if (deliveryDetail.truckOrganized) {
        setTruckOrganized(deliveryDetail.truckOrganized === 'Yes' ? 'yes' : 'no');
      }
      if (deliveryDetail.checkerComments) {
        setComments(deliveryDetail.checkerComments);
      }
      if (deliveryDetail.checkerImageResponse) {
        try {
          const imgMap = JSON.parse(deliveryDetail.checkerImageResponse) as Record<string, string>;
          // Normalize to id-keyed maps: support legacy label keys and numeric id keys
          const configById = new Map(activePhotos.map(p => [String(p.id), p] as const));
          const configByLabel = new Map(activePhotos.map(p => [p.photoLabel.trim().toLowerCase(), p] as const));

          const fileNameMap: Record<string, string> = {};
          const urlMap: Record<string, string> = {};
          const extraPhotoItems: PhotoCategoryItem[] = [];
          let syntheticPhotoId = -2000;

          Object.entries(imgMap).forEach(([rawKey, val]) => {
            const key = String(rawKey).trim();
            let targetId: string | null = null;
            if (/^\d+$/.test(key)) {
              targetId = key;
            } else {
              const byLabel = configByLabel.get(key.toLowerCase());
              if (byLabel) targetId = String(byLabel.id);
            }
            if (!targetId) {
              const id = syntheticPhotoId--;
              extraPhotoItems.push({ id, photoLabel: rawKey, displayOrder: 9999, isActive: false });
              targetId = String(id);
            }

            // val may be fileName or SAS URL depending on flow; preserve as-is for file names map
            urlMap[targetId] = val;
            const withoutQuery = String(val).split('?')[0];
            fileNameMap[targetId] = decodeURIComponent(withoutQuery.split('/').pop() ?? val);
          });

          if (extraPhotoItems.length > 0) {
            const merged = [...activePhotos, ...extraPhotoItems];
            // Re-apply route type filtering to include synthetic items
            const filtered = filterPhotoCategoriesByRouteType(
              merged,
              resolvedRouteTypeId,
              Array.from(new Set([...Array.from(checkerImageResponseKeys)])),
              rtData,
            );
            setPhotoCategories(filtered);
          }

          setCheckerImageFileNames(fileNameMap);
          setCheckerImageUrls(urlMap);
        } catch (e) {
          console.warn('Failed to parse checkerImageResponse:', e);
        }
      }

      setCheckerItems(
        filterEquipmentByRouteType(
          resolvedCheckerItems,
          resolvedRouteTypeId,
          Array.from(new Set([
            ...Array.from(certResponseKeys),
            ...Array.from(checkerResponseKeys),
          ])),
          rtData,
        ),
      );
    } catch (e: any) {
      console.error('Failed to load delivery details:', e);
      setApiError(t('review.error_failed_load'));
    } finally {
      setIsLoading(false);
    }
  }, [record, t, currentLanguage]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleEquipmentChange = (id: number, val: EquipmentValue) => {
    setCheckerEquipment(prev => ({ ...prev, [id]: val }));
    setHasUnsavedChanges(true);
    if (val !== 'yes') {
      setCheckerRemarks(prev => {
        const c = { ...prev } as Record<number, string>;
        delete c[id];
        return c;
      });
    }
  };
  const handleCheckerRemarksChange = (id: number, text: string) => {
    setCheckerRemarks(prev => ({ ...prev, [id]: text }));
    setHasUnsavedChanges(true);
  };

  const handleCheckerPhotoUploaded = (label: string, data: { fileName: string; imageUrl: string }) => {
    // Accept either a photoLabel or a category id; store maps keyed by category id
    const matched = photoCategories.find(p => p.photoLabel === label) ?? photoCategories.find(p => String(p.id) === String(label));
    const key = matched ? String(matched.id) : String(label);
    setCheckerImageFileNames(prev => ({ ...prev, [key]: data.fileName }));
    setCheckerImageUrls(prev => ({ ...prev, [key]: data.imageUrl }));
    setHasUnsavedChanges(true);
  };

  const handleImagePreview = (uri: string | null) => {
    if (uri && uri.trim() !== '') {
      setPreviewImageUri(uri);
      setShowPreviewModal(true);
    }
  };

  // Handle navigation with unsaved changes
  const handleNavigationAttempt = (callback: () => void) => {
    const editableStatus = detail?.status === 'Pending Check' || detail?.status === 'In Progress - Checker';
    if (hasUnsavedChanges && editableStatus && canSubmit) {
      setPendingNavigation(() => callback);
      setShowUnsavedDialog(true);
    } else {
      callback();
    }
  };

  // Expose navigation guard to parent via ref
  useImperativeHandle(ref, () => ({ handleNavigationAttempt }));

  // Intercept Android hardware back button and browser tab close
  useEffect(() => {
    if (Platform.OS === 'android') {
      const subscription = BackHandler.addEventListener('hardwareBackPress', () => {
        handleNavigationAttempt(onBack);
        return true;
      });
      return () => subscription.remove();
    }
    if (Platform.OS === 'web') {
      const editableStatus = detail?.status === 'Pending Check' || detail?.status === 'In Progress - Checker';
      const handleBeforeUnload = (e: BeforeUnloadEvent) => {
        if (hasUnsavedChanges && editableStatus && canSubmit) {
          e.preventDefault();
          e.returnValue = '';
        }
      };
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUnsavedChanges, detail?.status, canSubmit, onBack]);

  // Save draft
  const handleSaveDraft = async () => {
    if (!detail) return;

    try {
      setIsSavingDraft(true);
      setSubmitError('');

      // Build checkerResponse JSON if there are changes (ID-based, with fallback to legacy name keys)
      const draftPayload: CheckerVerificationRequest = {
        status: 'In Progress - Checker',
        ...(Object.keys(checkerEquipment).length > 0 && {
          checkerResponse: JSON.stringify(
            Object.fromEntries(
              checkerItems.map(item => {
                const val = checkerEquipment[item.id];
                const remarks = (checkerRemarks as Record<number,string>)[item.id]?.trim();
                const entry = serializeCheckerResponseEntry(item, val ?? null, remarks ?? '');
                // Prefer ID-based key
                if (entry === null) return [String(item.id), null];
                return [String(item.id), entry];
              })
            )
          ),
        }),
        ...(truckOrganized && {
          truckOrganized: truckOrganized === 'yes' ? 'Yes' : truckOrganized === 'no' ? 'No' : undefined,
        }),
        ...(comments && { checkerComments: comments }),
        ...(Object.keys(checkerImageFileNames).length > 0 && {
          checkerImageResponse: JSON.stringify(checkerImageFileNames),
        }),
        // Capture checker equipment snapshot only if not already set (first save)
        // ...(detail && !detail.checkerConfigSnapshot && {
        //   checkerConfigSnapshot: JSON.stringify(
        //     checkerItems.map(item => ({
        //       id: item.id,
        //       equipmentName: item.equipmentName,
        //       checkerPosLabel: item.checkerPosLabel,
        //       checkerNegLabel: item.checkerNegLabel,
        //       showOnCheckerForm: item.showOnCheckerForm,
        //       isActive: item.isActive,
        //       displayOrder: item.displayOrder,
        //     }))
        //   ),
        // }),
      };

      await submitCheckerVerification(detail.id, draftPayload);
      setHasUnsavedChanges(false);
      setShowUnsavedDialog(false);
      toast.showToast({ header: t('review.toast_draft_header'), subtext: t('review.toast_draft_subtext'), variant: 'success' });

      // Execute pending navigation
      if (pendingNavigation) {
        pendingNavigation();
        setPendingNavigation(null);
      }
    } catch (e: any) {
      console.error('Failed to save checker draft:', e);
      setSubmitError(t('review.error_failed_save_draft'));
    } finally {
      setIsSavingDraft(false);
    }
  };

  // Track changes to form fields
  const handleTruckOrganizedChange = (value: EquipmentValue) => {
    setTruckOrganized(value);
    setHasUnsavedChanges(true);
  };

  const handleCommentsChange = (value: string) => {
    setComments(value);
    setHasUnsavedChanges(true);
  };

  const handleVerify = async (status: 'Approved' | 'Rejected') => {
    if (!detail) return;

    if (status === 'Approved') {
      // All equipment items (ShowOnCheckerForm = true) must have a selection
      const unanswered = checkerItems.filter(item => !checkerEquipment[item.id]);
      if (unanswered.length > 0) {
        setSubmitError(t('review.error_select_equipment', { items: unanswered.map(i => i.equipmentName).join(', ') }));
        return;
      }
      // Truck Organized must have a selection
      if (!truckOrganized) {
        setSubmitError(t('review.error_truck_organized'));
        return;
      }
    }

    if (status === 'Rejected') {
      // Comments are required when rejecting
      if (!comments.trim()) {
        setSubmitError(t('review.error_comments_required'));
        return;
      }
    }

    // Build checkerResponse JSON: use ID-based keys with language-independent values
    const checkerResponse = JSON.stringify(
      Object.fromEntries(
        checkerItems.map(item => {
          const val = checkerEquipment[item.id] ?? null;
          const remarks = (checkerRemarks as Record<number,string>)[item.id]?.trim() ?? '';
          const entry = serializeCheckerResponseEntry(item, val, remarks);
          if (entry === null) return [String(item.id), null];
          return [String(item.id), entry];
        })
      )
    );

    const payload: CheckerVerificationRequest = {
      checkerResponse,
      truckOrganized: truckOrganized === 'yes' ? 'Yes' : truckOrganized === 'no' ? 'No' : undefined,
      checkerComments: comments || undefined,
      checkerImageResponse: Object.keys(checkerImageFileNames).length > 0
        ? JSON.stringify(checkerImageFileNames)
        : undefined,
      // // Capture checker equipment snapshot only if not already set (first submission)
      // ...(detail && !detail.checkerConfigSnapshot && {
      //   checkerConfigSnapshot: JSON.stringify(
      //     checkerItems.map(item => ({
      //       id: item.id,
      //       equipmentName: item.equipmentName,
      //       checkerPosLabel: item.checkerPosLabel,
      //       checkerNegLabel: item.checkerNegLabel,
      //       showOnCheckerForm: item.showOnCheckerForm,
      //       isActive: item.isActive,
      //       displayOrder: item.displayOrder,
      //     }))
      //   ),
      // }),
      status,
    };

    try {
      setSubmitError('');
      setIsSubmitting(true);

      // Re-fetch current status to detect concurrent modifications
      const latestDetail = await fetchDeliveryDetail(detail.id);
      if (latestDetail.status === 'Approved' || latestDetail.status === 'Rejected') {
        const checkerName = latestDetail.checkerName
          ? formatUserName(latestDetail.checkerName)
          : t('review.another_checker');
        setStaleMessage(
          t('review.stale_message', { status: t(`status.${latestDetail.status}`, { defaultValue: latestDetail.status }), checker: checkerName })
        );
        setShowStaleDialog(true);
        return;
      }

      await submitCheckerVerification(detail.id, payload);
      toast.showToast({
        header: status === 'Approved' ? t('review.toast_approved_header') : t('review.toast_rejected_header'),
        subtext: status === 'Approved' ? t('review.toast_approved_subtext') : t('review.toast_rejected_subtext'),
        variant: 'success',
      });
      if (status === 'Approved') onApprove();
      else onReject();
    } catch (e: any) {
      console.error('Failed to submit checker verification:', e);
      setSubmitError(t('review.error_submission_failed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const displayStatus = (detail?.status ?? record?.status ?? 'Pending Check') as DeliveryStatus;
  const statusCfg = resolvedTheme === 'dark'
    ? (STATUS_CONFIG_DARK[displayStatus] ?? { bg: '#2B2B2B', color: '#FFFFFF' })
    : (STATUS_CONFIG[displayStatus] ?? { bg: AdminColors.gray50, color: AdminColors.gray450 });
  const isDarkTheme = resolvedTheme === 'dark';
  const pillPosBg = isDarkTheme ? '#1A6B24' : AdminColors.green75;
  const pillNegBg = isDarkTheme ? '#9B2626' : AdminColors.red75;
  const pillPosText = isDarkTheme ? '#FFFFFF' : AdminColors.green600;
  const pillNegText = isDarkTheme ? '#FFFFFF' : AdminColors.red400;
  // Editable in review when status is 'Pending Check' (first open) or 'In Progress - Checker' (saved draft)
  const isReadOnly = forceReadOnly || (displayStatus !== 'Pending Check' && displayStatus !== 'In Progress - Checker');
  // Show Certifier Submission only when status is Approved or Rejected
  const showCertifierSubmission = displayStatus === 'Approved' || displayStatus === 'Rejected';

  const certifierDisplayItems: Array<{ question: string; response: string; remarks?: string | null }> = detail
    ? parseCertifierDisplayItems(detail, equipmentConfig)
    : [];

  // Parse checkerResponse JSON for read-only display
  const checkerMap: Record<string, string | null> = (() => {
    if (!detail?.checkerResponse) return {};
    try { return JSON.parse(detail.checkerResponse); } catch { return {}; }
  })();

  const checkerDisplayItems: Array<{ question: string; response: string; remarks?: string | null }> =
    detail ? parseCheckerDisplayItems(detail, equipmentConfig) : [];

  // Parse certifierImageResponse JSON: resolves numeric id keys -> photoLabel for display
  // Falls back to the raw key when no matching category found (legacy label keys pass through)
  const imageResponseMap: Record<string, string | null> = (() => {
    if (!detail?.certifierImageResponse) return {};
    try {
      const parsed = JSON.parse(detail.certifierImageResponse) as Record<string, unknown>;
      // Build lookup: category id (string) -> photoLabel
      const catById = new Map([...photoCategories].map(p => [String(p.id), p.photoLabel] as const));
      return Object.fromEntries(
        Object.entries(parsed)
          .filter(([, url]) => url === null || typeof url === 'string')
          .map(([key, url]) => {
            const resolvedLabel = /^\d+$/.test(key.trim()) ? (catById.get(key.trim()) ?? key) : key;
            return [resolvedLabel, url as string | null];
          }),
      );
    } catch { return {}; }
  })();

  // Parse checkerImageResponse JSON: resolves numeric id keys -> photoLabel for display
  const checkerImageResponseMap: Record<string, string | null> = (() => {
    if (!detail?.checkerImageResponse) return {};
    try {
      const parsed = JSON.parse(detail.checkerImageResponse) as Record<string, unknown>;
      const catById = new Map([...photoCategories].map(p => [String(p.id), p.photoLabel] as const));
      return Object.fromEntries(
        Object.entries(parsed)
          .filter(([, url]) => url === null || typeof url === 'string')
          .map(([key, url]) => {
            const resolvedLabel = /^\d+$/.test(key.trim()) ? (catById.get(key.trim()) ?? key) : key;
            return [resolvedLabel, url as string | null];
          }),
      );
    } catch { return {}; }
  })();

  if (!record) {
    return (
      <View style={styles.loadingWrapper}>
        <Text style={styles.errorText}>{t('review.error_no_record')}</Text>
      </View>
    );
  }

  if (isLoading) {
    return (
      <View style={styles.loadingWrapper}>
        <ActivityIndicator size="large" color={dc(AdminColors.blue700)} />
      </View>
    );
  }

  if (apiError) {
    return (
      <View style={styles.loadingWrapper}>
        <MaterialIcons name="error-outline" size={24} color={dc(AdminColors.red400)} />
        <Text style={styles.errorText}>{apiError}</Text>
        <TouchableOpacity onPress={loadData}>
          <Text style={styles.retryText}>{t('review.error_retry')}</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container} contentContainerStyle={[styles.contentContainer, isMobile && styles.contentContainerMobile]}>
      {forceReadOnly ? (
        <View style={styles.readOnlyBanner}>
          <MaterialIcons name="visibility" size={16} color={dc(AdminColors.blue700)} />
          <Text style={styles.readOnlyBannerText}>{t('create.banner_read_only')}</Text>
        </View>
      ) : null}

      {/* Record header */}
      <View style={styles.reviewHeader}>
        <View style={{ flex: 1 }}>
          <Text style={styles.reviewRoute}>{detail?.routeId ?? record.routeId}</Text>
          <View style={styles.reviewMeta}>
            <Text style={styles.reviewMetaText}>{(() => {
              const routeTypeLabel = resolveRouteTypeLabel(detail?.routeType ?? record.routeType, routeTypes);
              return t(`route_types.${routeTypeLabel.toLowerCase()}`, routeTypeLabel);
            })()}</Text>
            <Text style={styles.reviewMetaText}>{t('review.label_asset')} {detail?.assetId ?? record.assetId}</Text>
            <Text style={styles.reviewMetaText}>{t('review.label_delivery')} {formatDate(detail?.deliveryDate ?? record.deliveryDate)}</Text>
          </View>
          {isMobile && (
            <View style={{ marginTop: 8, gap: 6 }}>
              {detail?.certifierName && (
                <Text style={styles.reviewMetaText}>
                  {t('review.label_certified_by')} <Text style={{ fontFamily: FontFamily.bold }}>{formatUserName(detail.certifierName)}</Text>
                  {detail?.createdAt ? ` ${t('review.label_at')} ${formatTimeAmPm(detail.createdAt)} ${t('review.label_on')} ${formatDate(detail.createdAt)}` : ''}
                </Text>
              )}
              {detail?.checkerName && (
                <Text style={styles.reviewMetaText}>
                  {t('review.label_verified_by')} <Text style={{ fontFamily: FontFamily.bold }}>{formatUserName(detail.checkerName)}</Text>
                  {detail?.modifiedAt ? ` ${t('review.label_at')} ${formatTimeAmPm(detail.modifiedAt)} ${t('review.label_on')} ${formatDate(detail.modifiedAt)}` : ''}
                </Text>
              )}
            </View>
          )}
        </View>
        {isMobile ? (
          <View style={[styles.badge, styles.reviewHeaderBadgeMobile, { backgroundColor: statusCfg.bg }]}> 
            <Text style={[styles.badgeText, { color: statusCfg.color }]}>
              {t(`status.${displayStatus}`, { defaultValue: displayStatus })}
            </Text>
          </View>
        ) : (
          <View style={[styles.badge, { backgroundColor: statusCfg.bg }]}> 
            <Text style={[styles.badgeText, { color: statusCfg.color }]}>
              {t(`status.${displayStatus}`, { defaultValue: displayStatus })}
            </Text>
          </View>
        )}
      </View>

      {/* Split layout */}
      <View style={[styles.split, isMobile && styles.splitMobile]}>

        {/* LEFT — Certifier submission (read-only) — only visible when Approved or Rejected */}
        {showCertifierSubmission && (
          <View style={styles.splitPane}>
          <View style={[styles.card, isMobile && styles.cardMobile]} onLayout={e => setCertifierCardWidth(e.nativeEvent.layout.width)}>
            <View style={styles.formTitle}>
              <Text style={styles.formTitleText}>{t('review.section_certifier')}</Text>
            </View>
            <Text style={styles.submittedBy}>
              {t('review.label_certified_by')}{' '}
              <Text style={styles.submittedByName}>
                {detail?.certifierName ? formatUserName(detail.certifierName) : t('review.label_certifier_fallback')}
              </Text>
              {detail?.createdAt ? ` ${t('review.label_at')} ${formatTimeAmPm(detail.createdAt)} ${t('review.label_on')} ${formatDate(detail.createdAt)}` : ''}
            </Text>

            {/* Photos */}
            {Object.keys(imageResponseMap).length > 0 ? (
              <>
                <Text style={styles.sectionLabel}>{t('review.section_equipment_photos')}</Text>
                {isMobile ? (
                  <View style={styles.photoRowMobile}>
                    {Object.entries(imageResponseMap).map(([category, uri]) => {
                      const innerWidth = certifierCardWidth - CARD_PADDING;
                      const PHOTO_COLS = 3;
                      const PHOTO_GAP_MOBILE = 8;
                      const totalGaps = (PHOTO_COLS - 1) * PHOTO_GAP_MOBILE;
                      const thumbWidth = innerWidth > 0 ? Math.floor((innerWidth - totalGaps) / PHOTO_COLS) : undefined;
                      return (
                        <View key={category} style={[styles.photoThumbWrapper, thumbWidth ? { flex: 0, width: thumbWidth } : undefined]}>
                          {uri && uri.trim() !== '' ? (
                            <TouchableOpacity activeOpacity={0.7} onPress={() => handleImagePreview(uri)}>
                              <Image source={{ uri }} style={styles.photoThumb} resizeMode="cover" />
                            </TouchableOpacity>
                          ) : (
                            <View style={[styles.photoThumb, styles.photoPlaceholder]}>
                              <MaterialIcons name="photo-camera" size={28} color={dc(AdminColors.textMuted)} />
                            </View>
                          )}
                          <Text style={styles.photoThumbLabel} numberOfLines={1} ellipsizeMode="tail">{category}</Text>
                        </View>
                      );
                    })}
                  </View>
                ) : (
                  <View
                    style={[
                      styles.photoRowDesktop,
                      Object.keys(imageResponseMap).length > MAX_VISIBLE && Platform.OS === 'web' && { overflow: 'scroll' as any },
                    ]}
                  >
                    {Object.entries(imageResponseMap).map(([category, uri]) => {
                      const PHOTO_GAP_DESKTOP = 10;
                      const totalGaps = (MAX_VISIBLE - 1) * PHOTO_GAP_DESKTOP;
                      const thumbWidth = certifierCardWidth > 0
                        ? Math.floor((certifierCardWidth - CARD_PADDING - totalGaps) / MAX_VISIBLE)
                        : undefined;
                      return (
                        <View
                          key={category}
                          style={thumbWidth ? { width: thumbWidth, flexGrow: 0, flexShrink: 0 } : { flex: 1 }}
                        >
                          {uri && uri.trim() !== '' ? (
                            <TouchableOpacity activeOpacity={0.7} onPress={() => handleImagePreview(uri)}>
                              <Image source={{ uri }} style={styles.photoThumb} resizeMode="cover" />
                            </TouchableOpacity>
                          ) : (
                            <View style={[styles.photoThumb, styles.photoPlaceholder]}>
                              <MaterialIcons name="photo-camera" size={28} color={dc(AdminColors.textMuted)} />
                            </View>
                          )}
                          <Text style={styles.photoThumbLabel} numberOfLines={1} ellipsizeMode="tail">{category}</Text>
                        </View>
                      );
                    })}
                  </View>
                )}
              </>
            ) : null}

            {/* Equipment */}
            <Text style={styles.sectionLabel}>{t('review.section_equipment_status')}</Text>
            {certifierDisplayItems.length > 0 ? (
              certifierDisplayItems.map((item, idx) => {
                const display = item.response;
                const remarks = item.remarks ?? '';
                const hasRemarks = remarks.trim() !== '';
                const lc = String(display || '').toLowerCase();
                const isPositive = display && !lc.startsWith('not') && lc !== 'no';
                return (
                  <View key={idx}>
                    <View style={[isMobile ? styles.equipmentTileMobile : styles.readOnlyRow, isMobile && idx % 2 === 1 && styles.equipmentTileMobileAlt, hasRemarks && { borderBottomWidth: 0 }]}>
                      <Text style={styles.equipmentLabel}>{item.question}</Text>
                      <View style={[styles.readOnlyVal, {
                        backgroundColor: isPositive ? pillPosBg : pillNegBg,
                      }]}>
                        <Text style={[styles.readOnlyValText, {
                          color: isPositive ? pillPosText : pillNegText,
                        }]}>
                          {display || 'N/A'}
                        </Text>
                      </View>
                    </View>
                    {hasRemarks ? (
                      <View style={styles.commentsFieldInline}>
                        <Text style={styles.detailsLabel}>{t('review.label_additional_details')}</Text>
                        <View style={styles.commentsReadOnly}>
                          <Text style={styles.commentsReadOnlyText}>{remarks}</Text>
                        </View>
                      </View>
                    ) : null}
                  </View>
                );
              })
            ) : (
              <Text style={styles.noteText}>{t('review.note_no_equipment_data')}</Text>
            )}

            {detail?.certifierComments ? (
              <View style={styles.commentsField}>
                <Text style={styles.fieldLabel}>{t('review.label_certifier_comments')}</Text>
                <View style={styles.commentsReadOnly}>
                  <Text style={styles.commentsReadOnlyText}>{detail.certifierComments}</Text>
                </View>
              </View>
            ) : null}
          </View>
        </View>
        )}

        {/* RIGHT — Checker verification */}
        <View style={styles.splitPane}>
          <View style={[styles.card, isMobile && styles.cardMobile]} onLayout={e => setCheckerCardWidth(e.nativeEvent.layout.width)}>
            <View style={styles.formTitle}>
              <Text style={styles.formTitleText}>{t('review.section_checker')}</Text>
            </View>

            {isReadOnly ? (
              /* ── Read-only mode when already verified ── */
              <>
                <Text style={styles.submittedBy}>
                  {t('review.label_verified_by')}{' '}
                  <Text style={styles.submittedByName}>
                    {detail?.checkerName ? formatUserName(detail.checkerName) : t('review.label_checker_fallback')}
                  </Text>
                  {detail?.modifiedAt ? ` ${t('review.label_at')} ${formatTimeAmPm(detail.modifiedAt)} ${t('review.label_on')} ${formatDate(detail.modifiedAt)}` : ''}
                </Text>

                {/* Checker Photos (read-only) */}
                {Object.keys(checkerImageResponseMap).length > 0 ? (
                  <>
                    <Text style={styles.sectionLabel}>{t('review.section_equipment_photos')}</Text>
                    {isMobile ? (
                      <View style={styles.photoRowMobile}>
                        {Object.entries(checkerImageResponseMap).map(([category, uri]) => {
                          const innerWidth = checkerCardWidth - CARD_PADDING;
                          const PHOTO_COLS = 3;
                          const PHOTO_GAP_MOBILE = 8;
                          const totalGaps = (PHOTO_COLS - 1) * PHOTO_GAP_MOBILE;
                          const thumbWidth = innerWidth > 0 ? Math.floor((innerWidth - totalGaps) / PHOTO_COLS) : undefined;
                          return (
                            <View key={category} style={[styles.photoThumbWrapper, thumbWidth ? { flex: 0, width: thumbWidth } : undefined]}>
                              {uri && uri.trim() !== '' ? (
                                <TouchableOpacity activeOpacity={0.7} onPress={() => handleImagePreview(uri)}>
                                  <Image source={{ uri }} style={styles.photoThumb} resizeMode="cover" />
                                </TouchableOpacity>
                              ) : (
                                <View style={[styles.photoThumb, styles.photoPlaceholder]}>
                                  <MaterialIcons name="photo-camera" size={28} color={dc(AdminColors.textMuted)} />
                                </View>
                              )}
                              <Text style={styles.photoThumbLabel} numberOfLines={1} ellipsizeMode="tail">{category}</Text>
                            </View>
                          );
                        })}
                      </View>
                    ) : (
                      <View
                        style={[
                          styles.photoRowDesktop,
                          Object.keys(checkerImageResponseMap).length > MAX_VISIBLE && Platform.OS === 'web' && { overflow: 'scroll' as any },
                        ]}
                      >
                        {Object.entries(checkerImageResponseMap).map(([category, uri]) => {
                          const PHOTO_GAP_DESKTOP = 10;
                          const totalGaps = (MAX_VISIBLE - 1) * PHOTO_GAP_DESKTOP;
                          const thumbWidth = checkerCardWidth > 0
                            ? Math.floor((checkerCardWidth - CARD_PADDING - totalGaps) / MAX_VISIBLE)
                            : undefined;
                          return (
                            <View
                              key={category}
                              style={thumbWidth ? { width: thumbWidth, flexGrow: 0, flexShrink: 0 } : { flex: 1 }}
                            >
                              {uri && uri.trim() !== '' ? (
                                <TouchableOpacity activeOpacity={0.7} onPress={() => handleImagePreview(uri)}>
                                  <Image source={{ uri }} style={styles.photoThumb} resizeMode="cover" />
                                </TouchableOpacity>
                              ) : (
                                <View style={[styles.photoThumb, styles.photoPlaceholder]}>
                                  <MaterialIcons name="photo-camera" size={28} color={dc(AdminColors.textMuted)} />
                                </View>
                              )}
                              <Text style={styles.photoThumbLabel} numberOfLines={1} ellipsizeMode="tail">{category}</Text>
                            </View>
                          );
                        })}
                      </View>
                    )}
                  </>
                ) : null}

                <Text style={styles.sectionLabel}>{t('review.section_equipment_status')}</Text>
                {checkerDisplayItems.length > 0 ? (
                  checkerDisplayItems.map((item, idx) => {
                    const display = item.response;
                    const remarks = item.remarks ?? '';
                    const hasRemarks = remarks.trim() !== '';
                    const lc = String(display || '').toLowerCase();
                    const isPositive = display && !lc.startsWith('not') && lc !== 'no';
                    return (
                      <View key={idx}>
                        <View style={[isMobile ? styles.equipmentTileMobile : styles.readOnlyRow, isMobile && idx % 2 === 1 && styles.equipmentTileMobileAlt, hasRemarks && { borderBottomWidth: 0 }]}>
                          <Text style={styles.equipmentLabel}>{item.question}</Text>
                          <View style={[styles.readOnlyVal, {
                            backgroundColor: isPositive ? pillPosBg : pillNegBg,
                          }]}>
                            <Text style={[styles.readOnlyValText, {
                              color: isPositive ? pillPosText : pillNegText,
                            }]}> 
                              {display || 'N/A'}
                            </Text>
                          </View>
                        </View>
                        {hasRemarks ? (
                          <View style={styles.commentsFieldInline}>
                            <Text style={styles.detailsLabel}>{t('review.label_additional_details')}</Text>
                            <View style={styles.commentsReadOnly}>
                              <Text style={styles.commentsReadOnlyText}>{remarks}</Text>
                            </View>
                          </View>
                        ) : null}
                      </View>
                    );
                  })
                ) : (
                  <Text style={styles.noteText}>{t('review.note_no_equipment_data')}</Text>
                )}

                <View style={[isMobile ? styles.equipmentTileMobile : styles.readOnlyRow]}>
                  <Text style={styles.equipmentLabel}>{t('review.label_truck_organized')}</Text>
                  {detail?.truckOrganized ? (
                    <View style={[styles.readOnlyVal, {
                      backgroundColor: detail.truckOrganized === 'Yes' ? pillPosBg : pillNegBg,
                    }]}>
                      <Text style={[styles.readOnlyValText, {
                        color: detail.truckOrganized === 'Yes' ? pillPosText : pillNegText,
                      }]}>
                        {detail.truckOrganized}
                      </Text>
                    </View>
                  ) : (
                    <Text style={styles.noteText}>—</Text>
                  )}
                </View>

                {detail?.checkerComments ? (
                  <View style={styles.commentsField}>
                    <Text style={styles.fieldLabel}>{t('review.label_checker_comments')}</Text>
                    <View style={styles.commentsReadOnly}>
                      <Text style={styles.commentsReadOnlyText}>{detail.checkerComments}</Text>
                    </View>
                  </View>
                ) : null}
              </>
            ) : (
              /* ── Editable form when status is Pending Check ── */
              <>
                <Text style={styles.noteText}>{t('review.note_verify_items')}</Text>

                {/* Equipment Photos (editable) */}
                {photoCategories.length > 0 ? (
                  <>
                    <Text style={styles.sectionLabel}>{t('review.section_equipment_photos')}</Text>
                    {isMobile ? (
                      <View style={styles.photoRowMobile}>
                        {photoCategories.map(category => {
                          const label = category.photoLabel;
                          const innerWidth = checkerCardWidth - CARD_PADDING;
                          const PHOTO_COLS = 3;
                          const PHOTO_GAP_MOBILE = 8;
                          const totalGaps = (PHOTO_COLS - 1) * PHOTO_GAP_MOBILE;
                          const mobileBoxWidth = innerWidth > 0 ? Math.floor((innerWidth - totalGaps) / PHOTO_COLS) : undefined;
                          return (
                            <PTPhotoUpload
                              key={category.id}
                              label={label}
                              aspectRatio={1}
                              boxWidth={mobileBoxWidth}
                              initialImageUrl={checkerImageUrls[String(category.id)]}
                              onUploaded={data => handleCheckerPhotoUploaded(String(category.id), data)}
                            />
                          );
                        })}
                      </View>
                    ) : (
                      <View
                        style={[
                          styles.photoRowDesktop,
                          photoCategories.length > MAX_VISIBLE && Platform.OS === 'web' && { display: 'flex' as any, flexWrap: 'wrap' as any} // Wrap to multiple rows if exceeding max visible on desktop web,
                        ]}
                      >
                        {photoCategories.map(category => {
                          const label = category.photoLabel;
                          const PHOTO_GAP_DESKTOP = 10;
                          const totalGaps = (MAX_VISIBLE - 1) * PHOTO_GAP_DESKTOP;
                          const thumbWidth = checkerCardWidth > 0
                            ? Math.floor((checkerCardWidth - CARD_PADDING - totalGaps) / MAX_VISIBLE)
                            : undefined;
                          return (
                            <View
                              key={`${category.id}-photo-wrapper`}
                              style={thumbWidth ? { width: thumbWidth, flexGrow: 0, flexShrink: 0 } : { flex: 1 }}
                            >
                              <PTPhotoUpload
                                key={`${category.id}-${checkerImageFileNames[String(category.id)] ?? 'empty'}`}
                                label={label}
                                aspectRatio={16 / 10}
                                initialImageUrl={checkerImageUrls[String(category.id)]}
                                onUploaded={data => handleCheckerPhotoUploaded(String(category.id), data)}
                              />
                            </View>
                          );
                        })}
                      </View>
                    )}
                  </>
                ) : null}

                {/* Certifier Comments (read-only in editable form) */}
                {detail?.certifierComments ? (
                  <View style={styles.commentsField}>
                    <Text style={styles.fieldLabel}>{t('review.label_certifier_comments')}</Text>
                    <View style={styles.commentsReadOnly}>
                      <Text style={styles.commentsReadOnlyText}>{detail.certifierComments}</Text>
                    </View>
                  </View>
                ) : null}

                {checkerItems.map((item, idx) => (
                  <View key={item.id}>
                    <CheckerRow
                      item={item}
                      value={checkerEquipment[item.id] ?? null}
                      onChange={handleEquipmentChange}
                      isMobile={isMobile}
                      alt={idx % 2 === 1}
                      hasDetails={item.isTextBoxRequired && checkerEquipment[item.id] === 'yes'}
                    />
                    {item.isTextBoxRequired && checkerEquipment[item.id] === 'yes' ? (
                          <View style={styles.commentsFieldInline}>
                            <Text style={styles.detailsLabel}>{t('review.label_additional_details')}</Text>
                            <TextInput
                              style={[styles.commentsInput, styles.multilineInput]}
                              multiline
                              numberOfLines={3}
                              value={checkerRemarks[item.id] ?? ''}
                              onChangeText={t => handleCheckerRemarksChange(item.id, t)}
                            />
                          </View>
                        ) : null}
                  </View>
                ))}

                {checkerItems.length === 0 && (
                  <Text style={styles.noteText}>{t('review.note_no_checker_equipment')}</Text>
                )}

                <View style={{ marginTop: 14 }}>
                  <View style={[styles.equipmentRow, isMobile && styles.equipmentRowMobile]}>
                    <Text style={styles.equipmentLabel}>{t('review.label_truck_organized')}</Text>
                    <View style={styles.equipmentOpts}>
                      <TouchableOpacity
                        style={[styles.eqOption, truckOrganized === 'yes' && styles.eqOptionYes]}
                        onPress={() => handleTruckOrganizedChange(truckOrganized === 'yes' ? null : 'yes')}
                      >
                        <Text style={[styles.eqOptionText, truckOrganized === 'yes' && styles.eqOptionTextSelected]}>
                          {t('review.label_yes')}
                        </Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={[styles.eqOption, truckOrganized === 'no' && styles.eqOptionNo]}
                        onPress={() => handleTruckOrganizedChange(truckOrganized === 'no' ? null : 'no')}
                      >
                        <Text style={[styles.eqOptionText, truckOrganized === 'no' && styles.eqOptionTextSelected]}>
                          {t('review.label_no')}
                        </Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                </View>

                <View style={styles.commentsField}>
                  <Text style={styles.fieldLabel}>{t('review.label_checker_comments')}</Text>
                  <TextInput
                    style={styles.commentsInput}
                    multiline
                    numberOfLines={3}
                    placeholder={t('review.placeholder_comments')}
                    placeholderTextColor={dc(AdminColors.textMuted)}
                    value={comments}
                    onChangeText={handleCommentsChange}
                  />
                </View>

                {submitError ? (
                  <View style={[styles.validationError, isDarkTheme && { backgroundColor: '#9B2626' }]}>
                    <MaterialIcons name="error-outline" size={16} color={isDarkTheme ? '#FFFFFF' : AdminColors.red400} />
                    <Text style={styles.validationErrorText}>{submitError}</Text>
                  </View>
                ) : null}

                <View style={[styles.reviewActions, isMobile && styles.reviewActionsMobile]}>
                  <LhButton
                    text={t('review.btn_cancel')}
                    variant="secondary"
                    size={isMobile ? 'small' : 'medium'}
                    style={isMobile ? styles.actionBtnMobile : undefined}
                    textStyle={isMobile ? styles.actionBtnTextMobile : undefined}
                    onPress={() => handleNavigationAttempt(onBack)}
                    disabled={isSubmitting}
                  />
                  {canSubmit && (
                    <>
                      <LhButton
                        text={isSubmitting ? t('review.btn_submitting') : t('review.btn_reject')}
                        variant="danger"
                        size={isMobile ? 'small' : 'medium'}
                        style={isMobile ? styles.actionBtnMobile : undefined}
                        textStyle={isMobile ? styles.actionBtnTextMobile : undefined}
                        onPress={() => handleVerify('Rejected')}
                        disabled={isSubmitting}
                      />
                      <LhButton
                        text={isSubmitting ? t('review.btn_submitting') : t('review.btn_approve')}
                        variant="primary"
                        size={isMobile ? 'small' : 'medium'}
                        style={isMobile ? styles.actionBtnMobile : undefined}
                        textStyle={isMobile ? styles.actionBtnTextMobile : undefined}
                        onPress={() => handleVerify('Approved')}
                        disabled={isSubmitting}
                      />
                    </>
                  )}
                </View>
              </>
            )}
          </View>
        </View>
      </View>

      {/* Unsaved Changes Dialog */}
      <UnsavedChangesDialog
        visible={showUnsavedDialog}
        onSaveForLater={handleSaveDraft}
        onContinueEditing={() => {
          setShowUnsavedDialog(false);
          setPendingNavigation(null);
        }}
        onCloseWithoutSaving={() => {
          setShowUnsavedDialog(false);
          setHasUnsavedChanges(false);
          if (pendingNavigation) {
            pendingNavigation();
            setPendingNavigation(null);
          }
        }}
        isLoading={isSavingDraft}
      />

      {/* Stale Record Dialog */}
      <Modal
        visible={showStaleDialog}
        transparent
        animationType="fade"
        onRequestClose={() => {
          setShowStaleDialog(false);
          onBack();
        }}
      >
        <View style={styles.staleModalBackdrop}>
          <View style={styles.staleModalCard}>
            <MaterialIcons name="info-outline" size={28} color={dc(AdminColors.blue700)} style={{ marginBottom: 10 }} />
            <Text style={styles.staleModalTitle}>{t('review.stale_title')}</Text>
            <Text style={styles.staleModalMessage}>{staleMessage}</Text>
            <TouchableOpacity
              style={styles.staleModalBtn}
              onPress={() => {
                setShowStaleDialog(false);
                onBack();
              }}
            >
              <Text style={styles.staleModalBtnText}>{t('review.stale_btn_ok')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Image Preview Modal */}
      <Modal
        visible={showPreviewModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowPreviewModal(false)}
      >
        <Pressable style={styles.previewModalBackdrop} onPress={() => setShowPreviewModal(false)}>
          <View style={styles.previewModalContent}>
            {previewImageUri ? (
              <WebImage
                uri={previewImageUri}
                style={[styles.previewModalImage, { width: modalImageSize, height: modalImageSize }]}
                resizeMode="contain"
              />
            ) : null}
          </View>
        </Pressable>
      </Modal>
    </ScrollView>
  );
});

const baseStyles = StyleSheet.create({
  container: { flex: 1, backgroundColor: AdminColors.bgPage },
  contentContainer: { padding: 20 },
  contentContainerMobile: { padding: 12, paddingBottom: 32 },
  loadingWrapper: { flex: 1, alignItems: 'center', justifyContent: 'center', paddingVertical: 48, gap: 8 },
  readOnlyBanner: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    backgroundColor: AdminColors.blue25, borderRadius: 8, padding: 12, marginBottom: 16,
  },
  readOnlyBannerText: { fontFamily: FontFamily.medium, fontSize: 13, color: AdminColors.blue700, flex: 1 },
  errorText: { fontFamily: FontFamily.medium, fontSize: 13, color: AdminColors.red400 },
  retryText: { fontFamily: FontFamily.bold, fontSize: 13, color: AdminColors.blue700, marginTop: 4 },
  reviewHeader: {
    backgroundColor: AdminColors.bgSurface,
    borderRadius: 10,
    padding: 18,
    marginBottom: 20,
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    position: 'relative',
    ...Platform.select({
      web: { boxShadow: '0 0 24px rgba(0,94,192,0.08)' } as any,
      default: { elevation: 2 },
    }),
  },
  reviewHeaderBadgeMobile: {
    position: 'absolute',
    top: 18,
    right: 18,
    zIndex: 2,
  },
  reviewRoute: { fontFamily: FontFamily.bold, fontSize: 20, color: AdminColors.textPrimary },
  reviewMeta: { flexDirection: 'row', flexWrap: 'wrap', gap: 12, marginTop: 4 },
  reviewMetaText: { fontFamily: FontFamily.regular, fontSize: 12, color: AdminColors.textMuted },
  badge: { paddingHorizontal: 14, paddingVertical: 5, borderRadius: 12 },
  badgeText: { fontFamily: FontFamily.bold, fontSize: 13 },
  split: { flexDirection: 'row', gap: 20 },
  splitMobile: { flexDirection: 'column', gap: 0 },
  splitPane: { flex: 1 },
  card: {
    backgroundColor: AdminColors.bgSurface,
    borderRadius: 10,
    padding: 20,
    ...Platform.select({
      web: { boxShadow: '0 0 24px rgba(0,94,192,0.08)' } as any,
      default: { elevation: 2 },
    }),
  },
  cardMobile: {
    backgroundColor: AdminColors.bgSurface,
    borderRadius: 10,
    padding: 14,
    marginBottom: 12,
    ...Platform.select({
      web: { boxShadow: '0 1px 3px rgba(0,0,0,0.08)' } as any,
      default: { elevation: 1, shadowColor: '#000', shadowOpacity: 0.08, shadowRadius: 3, shadowOffset: { width: 0, height: 1 } },
    }),
  },
  formTitle: {
    borderBottomWidth: 2,
    borderBottomColor: AdminColors.borderTable,
    paddingBottom: 8,
    marginBottom: 12,
  },
  formTitleText: {
    fontFamily: FontFamily.bold,
    fontSize: 14,
    color: AdminColors.blue700,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  submittedBy: { fontFamily: FontFamily.regular, fontSize: 12, color: AdminColors.textMuted, marginTop: 8, marginBottom: 14 },
  submittedByName: { fontFamily: FontFamily.bold, color: AdminColors.textPrimary },
  sectionLabel: {
    fontFamily: FontFamily.bold,
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    color: AdminColors.textMuted,
    marginBottom: 8,
    marginTop: 14,
  },
  photoRow: { flexDirection: 'row', gap: 10, marginBottom: 14, flexWrap: 'wrap' },
  photoRowMobile: { flexDirection: 'row', gap: 8, marginBottom: 14, flexWrap: 'wrap' },
  photoRowDesktop: { flexDirection: 'row', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'flex-start' },
  photoThumbWrapper: { flex: 1, minWidth: 80 },
  photoThumb: {
    width: '100%',
    aspectRatio: 16 / 10,
    borderRadius: 8,
    backgroundColor: AdminColors.gray50,
  },
  photoPlaceholder: {
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1.5,
    borderColor: AdminColors.borderDefault,
    borderStyle: 'dashed',
  },
  photoThumbLabel: {
    fontFamily: FontFamily.bold,
    fontSize: 10,
    color: AdminColors.textMuted,
    textAlign: 'center',
    marginTop: 4,
  },
  readOnlyRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: AdminColors.gray50,
  },
  equipmentTileMobile: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1.5,
    borderColor: AdminColors.borderDefault,
    backgroundColor: AdminColors.bgSurface,
    marginBottom: 6,
  },
  equipmentTileMobileAlt: {
    backgroundColor: AdminColors.blue25,
  },
  readOnlyVal: { paddingHorizontal: 10, paddingVertical: 3, borderRadius: 12 },
  readOnlyValText: { fontFamily: FontFamily.bold, fontSize: 12 },
  noteText: {
    fontFamily: FontFamily.regular,
    fontSize: 11,
    color: AdminColors.textMuted,
    fontStyle: 'italic',
    marginTop: 8,
    marginBottom: 4,
  },
  errorBanner: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    backgroundColor: AdminColors.red75, borderRadius: 6, padding: 8, marginBottom: 10,
  },
  errorBannerBottom: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    backgroundColor: AdminColors.red75, borderRadius: 6, padding: 10, marginBottom: 12, marginTop: 8,
  },
  submitErrorText: { fontFamily: FontFamily.medium, fontSize: 12, color: AdminColors.red400, flex: 1 },
  validationError: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    backgroundColor: AdminColors.red75,
    borderLeftWidth: 3,
    borderLeftColor: AdminColors.red400,
    borderRadius: 6,
    padding: 12,
    marginTop: 12,
    marginBottom: 4,
  },
  validationErrorText: {
    flex: 1,
    fontFamily: FontFamily.medium,
    fontSize: 13,
    color: AdminColors.red400,
    lineHeight: 18,
  },
  equipmentRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: AdminColors.gray50,
  },
  equipmentRowMobile: {
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1.5,
    borderColor: AdminColors.borderDefault,
    borderBottomWidth: 1.5,
    borderBottomColor: AdminColors.borderDefault,
    backgroundColor: AdminColors.bgSurface,
    marginBottom: 6,
  },
  equipmentRowMobileAlt: {
    backgroundColor: AdminColors.blue25,
  },
  equipmentLabel: { fontFamily: FontFamily.bold, fontSize: 13, color: AdminColors.textPrimary, flex: 1 },
  equipmentOpts: { flexDirection: 'row', gap: 6 },
  eqOption: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1.5,
    borderColor: AdminColors.borderDefault,
    backgroundColor: AdminColors.bgSurface,
  },
  eqOptionYes: { backgroundColor: AdminColors.green600, borderColor: AdminColors.green600 },
  eqOptionNo: { backgroundColor: AdminColors.red400, borderColor: AdminColors.red400 },
  eqOptionText: { fontFamily: FontFamily.bold, fontSize: 12, color: AdminColors.textSecondary },
  eqOptionTextSelected: { color: AdminColors.textOnDark },
  commentsField: { marginTop: 14 },
  commentsFieldInline: {
    marginTop: 0,
    marginBottom: 8,
  },
  fieldLabel: { fontFamily: FontFamily.bold, fontSize: 12, color: AdminColors.textPrimary, marginBottom: 6 },
  detailsLabel: { fontFamily: FontFamily.medium, fontSize: 11, color: AdminColors.textMuted, marginBottom: 6 },
  commentsInput: {
    borderWidth: 1.5,
    borderColor: AdminColors.borderDefault,
    borderRadius: 8,
    padding: 12,
    fontFamily: FontFamily.regular,
    fontSize: 14,
    color: AdminColors.textPrimary,
    minHeight: 80,
    textAlignVertical: 'top',
  },
  multilineInput: {
    minHeight: 64,
    textAlignVertical: 'top',
  },
  reviewActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 10,
    marginTop: 16,
  },
  reviewActionsMobile: {
    flexDirection: 'row',
    alignItems: 'stretch',
    justifyContent: 'space-between',
    gap: 10,
  },
  actionBtnMobile: {
    flex: 1,
    height: 44,
    borderRadius: 22,
    paddingHorizontal: 6,
    justifyContent: 'center',
  },
  actionBtnTextMobile: {
    fontSize: 13,
  },
  commentsReadOnly: {
    borderWidth: 1.5,
    borderColor: AdminColors.borderDefault,
    borderRadius: 8,
    padding: 12,
    backgroundColor: AdminColors.gray50,
    minHeight: 60,
  },
  commentsReadOnlyText: {
    fontFamily: FontFamily.regular,
    fontSize: 14,
    color: AdminColors.textPrimary,
  },
  checkerPhotoGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    marginBottom: 14,
  },
  previewModalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.8)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  previewModalContent: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  previewModalImage: {
    borderRadius: 8,
  },
  staleModalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  staleModalCard: {
    backgroundColor: AdminColors.bgSurface,
    borderRadius: 12,
    padding: 24,
    width: '100%',
    maxWidth: 400,
    alignItems: 'center',
    ...Platform.select({
      web: { boxShadow: '0 4px 24px rgba(0,0,0,0.18)' } as any,
      default: { elevation: 8 },
    }),
  },
  staleModalTitle: {
    fontFamily: FontFamily.bold,
    fontSize: 16,
    color: AdminColors.textPrimary,
    marginBottom: 10,
    textAlign: 'center',
  },
  staleModalMessage: {
    fontFamily: FontFamily.regular,
    fontSize: 14,
    color: AdminColors.textSecondary,
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 20,
  },
  staleModalBtn: {
    backgroundColor: AdminColors.blue700,
    paddingHorizontal: 32,
    paddingVertical: 10,
    borderRadius: 8,
  },
  staleModalBtnText: {
    fontFamily: FontFamily.bold,
    fontSize: 14,
    color: AdminColors.textOnDark,
  },
});

export default PerfectTruckReview;